import type {
  AudioEncoderConfiguration,
  ILocalAudioTrack,
  IRemoteAudioTrack,
  VideoEncoderConfiguration,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
  ITrack,
} from 'agora-rtc-sdk-ng';

import { AnyObject, Callable, Maybe, Optional } from '@vimeo/core/types/generic';

import { browserConfig } from '../../configs/BrowserConfig';
import { liveMediaConfig } from '../../configs/LiveMediaConfig';
import type { IAgoraError, TAgoraSDK } from '../../types/agora';
import { EAgoraErrorCode } from '../../types/agora';
import { isDeviceInList, safelyForceTrackConstraints } from '../dom/media';
import { LiveError } from '../error/LiveError';
import { getCircularReplacer } from '../general/json';
import { omit } from '../general/object';

export const TRACKS_REGISTRY: Map<string, ILocalTrack> = new Map();

/**
 * @param pixels - count of aggregated pixels
 * @returns based on provided count of pixels return bracket of rendering effort
 */
export function getBracketByPixels(pixels: number): string {
  if (pixels >= 8_294_400) {
    return '>=4k';
  } else if (pixels >= 3_686_400) {
    return '>=2k';
  } else if (pixels >= 2_073_600) {
    return '>=1080p';
  } else if (pixels >= 921_600) {
    return '>=720p';
  } else if (pixels >= 518_400) {
    return '>=540p';
  } else if (pixels >= 407_040) {
    return '>=480p';
  } else if (pixels >= 230_400) {
    return '>=360p';
  } else {
    return '<360p';
  }
}

/**
 * Get active source track if state is not ended and frames are rendering.
 */
export function getActiveAgoraSourceTrack(
  agoraTrack?: Optional<ITrack>,
): Optional<MediaStreamTrack> {
  const sourceTrack: Optional<MediaStreamTrack> = agoraTrack?.getMediaStreamTrack() || null;
  return sourceTrack && !isAgoraTrackMuted(agoraTrack) && sourceTrack.readyState !== 'ended'
    ? sourceTrack
    : null;
}

/**
 * Agora does not provide classes references for instance check and direct access.
 * We have to guest that provided object is track using duck-typing approach.
 *
 * @param target - agora media track to check
 * @returns whether target object is instance of agora media track
 */
export function isAgoraTrack(target?: Optional<AnyObject>): target is ITrack {
  return Boolean(target && target['trackMediaType'] && target['play']);
}

/**
 * Get encoder config for provided track.
 */
export function getAgoraTrackEncoderConfig(track: ILocalAudioTrack): AudioEncoderConfiguration;
export function getAgoraTrackEncoderConfig(track: ILocalVideoTrack): VideoEncoderConfiguration;
export function getAgoraTrackEncoderConfig(
  track: ILocalTrack,
): VideoEncoderConfiguration | AudioEncoderConfiguration {
  return (track as any)['_encoderConfig'];
}

/**
 * @param target - agora media track to check
 * @returns whether agora track was muted
 */
export function isAgoraTrackMuted(target?: Optional<ITrack>): boolean {
  if (!target) {
    return true;
  }

  /**
   * Agora local tracks have muted field and allow to check it with single field.
   */
  if ('_muted' in target) {
    return target['_muted'] === true;
  } else {
    return false;
  }
}

/**
 * Filter possible nullable tracks and pick only existing for agora media sending.
 * Also agora allows to publish only enabled tracks so we should not include tracks with disabled state.
 * Do some light checks to ensure that returning objects are actual media tracks.
 */
export function filterTracks<T extends ITrack = ITrack>(
  ...tracks: Array<Optional<AnyObject>>
): Array<T> {
  return tracks.filter((it) => isAgoraTrack(it) && (it as any)['_enabled']) as Array<T>;
}

/**
 * Close local tracks and dispose them from memory.
 */
export function closeAgoraLocalTracks(...tracks: Array<Optional<AnyObject>>): void {
  filterTracks<ILocalTrack>(...tracks).forEach((it) => {
    TRACKS_REGISTRY.delete(it.getTrackId());
    if (it.trackMediaType === 'video') {
      (it as ILocalVideoTrack).unpipe();
    }
    it.close();
  });
}

/**
 * Unpack agora track and return deviceID from internals.
 */
export function getAgoraTrackDeviceId(track: ITrack): string | undefined {
  return track.getMediaStreamTrack().getSettings().deviceId;
}

/**
 * Wrapper around default output devices getter.
 * For specific firefox case adds synthetic `default` device for switching.
 *
 * @param agoraSDK - agora SDK object
 * @param skipPermissionCheck - whether initial permission check should be skipped
 * @returns list of active media devices
 */
export async function getDevicesList(
  agoraSDK: TAgoraSDK,
  skipPermissionCheck?: boolean,
): Promise<Array<MediaDeviceInfo>> {
  const devices: Array<MediaDeviceInfo> = await agoraSDK.getDevices(skipPermissionCheck);

  // If firefox and no default device defined, add synthetic one.
  // Do not duplicate default if it exists.
  if (
    browserConfig.BROWSER?.name === 'firefox' &&
    !devices.find((it) => it.kind === 'audiooutput' && it.deviceId === 'default')
  ) {
    return [
      ...devices,
      {
        deviceId: 'default',
        kind: 'audiooutput',
        label: 'Default',
        groupId: '',
        toJSON(): string {
          return JSON.stringify(omit(this, 'toJSON'), getCircularReplacer());
        },
      },
    ];
  }

  return devices;
}

/**
 * Set output device of the track.
 * For firefox specifically handles default track state.
 *
 * @param track - track object to change output for
 * @param deviceId - id of the output device
 */
export function setAgoraAudioTrackOutput(
  track: IRemoteAudioTrack,
  deviceId: string,
): Promise<void> {
  // Firefox browser specifically handles 'default' as '' in contrast to chrome and others.
  if (browserConfig.BROWSER?.name === 'firefox' && deviceId === 'default') {
    return track.setPlaybackDevice('');
  }

  return track.setPlaybackDevice(deviceId);
}

/**
 * Get current mic.
 * Create error/track packed response without exceptions and return it in an immutable way.
 */
export function createMicrophoneTrackSafely({
  agoraSDK,
  devices,
  deviceId,
  isMuted = false,
  isHardMuteEnabled = false,
  onTrackEnded,
}: {
  agoraSDK: TAgoraSDK;
  deviceId: Maybe<string>;
  devices: Array<MediaDeviceInfo>;
  isMuted?: boolean;
  isHardMuteEnabled?: boolean;
  onTrackEnded?: Callable;
}): Promise<IAgoraError | IMicrophoneAudioTrack> {
  return agoraSDK
    .createMicrophoneAudioTrack({
      microphoneId: deviceId && isDeviceInList(devices, deviceId) ? deviceId : undefined,
      encoderConfig: {
        stereo: liveMediaConfig.LOCAL_AUDIO.ENCODER.STEREO,
        sampleRate: liveMediaConfig.LOCAL_AUDIO.ENCODER.SAMPLE_RATE,
        sampleSize: liveMediaConfig.LOCAL_AUDIO.ENCODER.SAMPLE_SIZE,
        bitrate: liveMediaConfig.LOCAL_AUDIO.ENCODER.BITRATE,
      },
      DTX: liveMediaConfig.LOCAL_AUDIO.DISCONTINUOUS_TRANSMISSION,
      AEC: liveMediaConfig.LOCAL_AUDIO.ECHO_CANCELLATION,
      AGC: liveMediaConfig.LOCAL_AUDIO.AUTO_GAIN,
      ANS: liveMediaConfig.LOCAL_AUDIO.NOISE_SUPPRESSION,
    })
    .then((agoraTrack) => {
      // Device-loss signal that fires on the actual track regardless of resolved id.
      if (onTrackEnded) {
        agoraTrack.on('track-ended', onTrackEnded);
      }

      TRACKS_REGISTRY.set(agoraTrack.getTrackId(), agoraTrack);

      return (
        isHardMuteEnabled ? agoraTrack.setEnabled(!isMuted) : agoraTrack.setMuted(isMuted)
      ).then(() => agoraTrack);
    })
    .catch((error: IAgoraError) => error);
}

/**
 * Get current camera.
 * Create error/track packed response without exceptions and return it in an immutable way.
 */
export function createCameraTrackSafely({
  agoraSDK,
  devices,
  deviceId,
  isMuted = false,
  isHardMuteEnabled = false,
}: {
  agoraSDK: TAgoraSDK;
  deviceId: Maybe<string>;
  devices: Array<MediaDeviceInfo>;
  isMuted?: boolean;
  isHardMuteEnabled?: boolean;
}): Promise<IAgoraError | LiveError | ICameraVideoTrack> {
  if (!devices.length) {
    return Promise.resolve(
      new LiveError('No devices available.', { code: EAgoraErrorCode.NO_DEVICES }),
    );
  }

  return agoraSDK
    .createCameraVideoTrack({
      cameraId: deviceId && isDeviceInList(devices, deviceId) ? deviceId : undefined,
      encoderConfig: {
        bitrateMax: liveMediaConfig.LOCAL_MEDIA.BITRATE_MAX,
        bitrateMin: liveMediaConfig.LOCAL_MEDIA.BITRATE_MIN,
        frameRate: liveMediaConfig.LOCAL_MEDIA.CONSTRAINTS.frameRate,
        width: liveMediaConfig.LOCAL_MEDIA.CONSTRAINTS.width,
        height: liveMediaConfig.LOCAL_MEDIA.CONSTRAINTS.height,
      },
      optimizationMode: liveMediaConfig.LOCAL_MEDIA.OPTIMIZATION_MODE,
    })
    .then((agoraTrack) => {
      safelyForceTrackConstraints(
        agoraTrack.getMediaStreamTrack(),
        liveMediaConfig.LOCAL_MEDIA.CONSTRAINTS,
      );

      TRACKS_REGISTRY.set(agoraTrack.getTrackId(), agoraTrack);

      return (
        isHardMuteEnabled ? agoraTrack.setEnabled(!isMuted) : agoraTrack.setMuted(isMuted)
      ).then(() => agoraTrack);
    })
    .catch((error: IAgoraError) => error);
}

/**
 * Safely create screen track or receive error instead.
 */
export function createScreenTrackSafely({
  agoraSDK,
  onTrackEnded,
}: {
  agoraSDK: TAgoraSDK;
  onTrackEnded: Callable;
}): Promise<ILocalVideoTrack | IAgoraError> {
  return agoraSDK
    .createScreenVideoTrack(
      {
        optimizationMode: liveMediaConfig.LOCAL_SCREEN.OPTIMIZATION_MODE,
        encoderConfig: liveMediaConfig.LOCAL_SCREEN.ENCODER_PRESET,
        scalabiltyMode: liveMediaConfig.LOCAL_SCREEN.SVC_PRESET,
      },
      'disable',
    )
    .then((agoraTrack: ILocalVideoTrack) => {
      agoraTrack.on('track-ended', onTrackEnded);

      TRACKS_REGISTRY.set(agoraTrack.getTrackId(), agoraTrack);
      return agoraTrack;
    })
    .catch((error: IAgoraError) => error) as unknown as Promise<ILocalVideoTrack | IAgoraError>;
}

/**
 * Unsubscribe from client user media if it has anything published.
 */
export async function unsubscribeFromAllUserMedia(
  client: IAgoraRTCClient,
  user: IAgoraRTCRemoteUser,
): Promise<void> {
  await Promise.all([
    user.hasAudio ? client.unsubscribe(user, 'audio') : Promise.resolve(),
    user.hasVideo ? client.unsubscribe(user, 'video') : Promise.resolve(),
  ]);
}

/**
 * @param track - target agora track to get volume from
 * @param fallback - volume to use if no track to get it from
 * @returns volume from track and fallback to 'fallback' value if anything is wrong with track
 */
export function getTrackVolumeSafely(
  track: Maybe<ILocalAudioTrack | IRemoteAudioTrack>,
  fallback: number = 0,
): number {
  try {
    return track ? track.getVolumeLevel() : fallback;
  } catch (error) {
    return fallback;
  }
}

/**
 * Agora SDK advanced parameters setting.
 * Not exposed for public usage, but recommended in some cases by their dev team.
 *
 * @param agoraSdk - object containing agora SDK methods
 * @param parameter - key of parameter to set
 * @param value - value of parameter to set
 */
export function applyAgoraParameter(agoraSdk: TAgoraSDK, parameter: string, value: unknown): void {
  if ('setParameter' in agoraSdk) {
    (agoraSdk as AnyObject).setParameter(parameter, value);
  }
}
